import {Button} from "primereact/button";
import type {ReactNode} from "react";
import {useState} from "react";
import {useTranslation} from "react-i18next";

import type {EmailVerificationStatus} from "../../api/payload-types/authorize";
import {openProfileDialog} from "../../redux/accountManagementSlice";
import {selectEmail, selectEmailVerificationHint, selectEmailVerified, selectUsername} from "../../redux/authSlice";
import {requestEmailVerificationThunk} from "../../redux/emailVerificationThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {EMAIL_HINT_SNOOZE_KEY_PREFIX} from "../../util/emailHintKeys";

/** After closing, the notice stays hidden for this long (per user and browser). */
export const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

const snoozeKey = (username: string) => `${EMAIL_HINT_SNOOZE_KEY_PREFIX}${username}`;

/** Whether the timestamp stored under `key` is younger than `maxAgeMs`. Storage may be unavailable. */
function isRecent(key: string, maxAgeMs: number, now: number): boolean {
    try {
        const value = Number(localStorage.getItem(key));
        return Number.isFinite(value) && value > 0 && now - value < maxAgeMs;
    } catch {
        return false;
    }
}

/** Stores the current time under `key`; without storage the notice may simply come back earlier. */
function markNow(key: string): void {
    try {
        localStorage.setItem(key, String(Date.now()));
    } catch {
        // ignored, see above
    }
}

/** Whether the notice was closed by this user within the last {@link SNOOZE_MS}. */
export function isSnoozed(username: string, now: number = Date.now()): boolean {
    return isRecent(snoozeKey(username), SNOOZE_MS, now);
}

/** Validity of the verification link, as issued by account-management (EmailVerificationRegistry.VALIDITY). */
export const LINK_VALIDITY_MS = 24 * 60 * 60 * 1000;

/**
 * reva-rest learns about a redeemed link only with its next account sync (every 60 s); this margin covers that, so
 * a user who verifies and signs in again right away does not get the notice back.
 */
export const SYNC_MARGIN_MS = 5 * 60 * 1000;

const mailSentKey = (username: string) => `reva_email_hint_mail_sent_${username}`;

/**
 * Whether a verification mail was sent to this user recently enough that its link may still be redeemed (plus
 * {@link SYNC_MARGIN_MS}). Until then the notice stays hidden; if the link is never used, it comes back afterwards.
 */
export function isMailPending(username: string, now: number = Date.now()): boolean {
    return isRecent(mailSentKey(username), LINK_VALIDITY_MS + SYNC_MARGIN_MS, now);
}

/** Records that a verification mail just went out, see {@link isMailPending}. */
export function markMailSent(username: string): void {
    markNow(mailSentKey(username));
}

const emailChangeKey = (username: string) => `reva_email_change_pending_${username}`;

/**
 * #19351: the new address a verified one is being replaced with, while its confirmation link may still be redeemed
 * (plus {@link SYNC_MARGIN_MS}). Only the browser that requested the change knows it — GET /user/profile reports
 * the stored address, which stays the old one until the link is used.
 */
export function getPendingEmailChange(username: string, now: number = Date.now()): string | null {
    try {
        const stored = JSON.parse(localStorage.getItem(emailChangeKey(username)) ?? "null") as {
            email?: unknown;
            at?: unknown;
        } | null;
        if (!stored || typeof stored.email !== "string" || typeof stored.at !== "number") return null;
        return now - stored.at < LINK_VALIDITY_MS + SYNC_MARGIN_MS ? stored.email : null;
    } catch {
        return null;
    }
}

/** Remembers a requested address change, see {@link getPendingEmailChange}. */
export function markPendingEmailChange(username: string, email: string): void {
    try {
        localStorage.setItem(emailChangeKey(username), JSON.stringify({email, at: Date.now()}));
    } catch {
        // without storage the dialog just does not show the pending change
    }
}

export type SendState = "idle" | "sending" | EmailVerificationStatus | "ERROR";

/**
 * #19351: Unobtrusive notice below the user menu when the stored e-mail address is missing or not verified —
 * without a verified address the password cannot be reset by mail. For an unverified address one click lets
 * account-management send the verification link; for a missing address it links to the profile page.
 * Shown only where account-management lets the user request the link (`emailVerificationHint` from reva-rest).
 */
export default function EmailVerificationNotice() {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();

    const username = useRevaSelector(selectUsername);
    const email = useRevaSelector(selectEmail);
    const emailVerified = useRevaSelector(selectEmailVerified);
    const hintEnabled = useRevaSelector(selectEmailVerificationHint);

    const [closed, setClosed] = useState(() => isSnoozed(username) || isMailPending(username));
    const [sendState, setSendState] = useState<SendState>("idle");

    if (!hintEnabled || emailVerified || closed || !username || sendState === "ALREADY_VERIFIED") {
        return null;
    }

    const onClose = () => {
        markNow(snoozeKey(username));
        setClosed(true);
    };

    const onSend = async () => {
        setSendState("sending");
        const status = await dispatch(requestEmailVerificationThunk());
        // THROTTLED means a mail went out a moment ago - that link is just as usable.
        if (status === "SENT" || status === "THROTTLED") {
            markMailSent(username);
        }
        setSendState(status);
    };

    // #19351: opens the in-REVA profile dialog (same one the header user menu opens) instead of linking
    // out to accounts.immobrain.net.
    // Same text-button style as "Starkes Passwort generieren" in the profile dialog.
    const profileLink = (
        <Button
            id="emailHintProfileBtn"
            type="button"
            text
            size="small"
            icon="pi pi-pencil"
            label={email ? t("header.emailHint.changeAddress") : t("header.emailHint.enterAddress")}
            onClick={() => dispatch(openProfileDialog())}
        />
    );

    let body: ReactNode;
    if (!email || sendState === "NO_EMAIL") {
        body = (
            <>
                <p className="m-0">{t("header.emailHint.missing")}</p>
                <p className="m-0 mt-2">{profileLink}</p>
            </>
        );
    } else if (sendState === "SENT") {
        body = <p className="m-0">{t("header.emailHint.sent", {email})}</p>;
    } else {
        const feedback: Partial<Record<SendState, string>> = {
            THROTTLED: t("header.emailHint.throttled"),
            NOT_ELIGIBLE: t("header.emailHint.notEligible"),
            ERROR: t("header.emailHint.error"),
        };
        body = (
            <>
                <p className="m-0">{t("header.emailHint.unverified", {email})}</p>
                {feedback[sendState] && <p className="m-0 mt-2 font-semibold">{feedback[sendState]}</p>}
                <div className="flex items-center gap-3 mt-3">
                    <Button
                        id="emailHintSendBtn"
                        size="small"
                        icon="pi pi-envelope"
                        label={t("header.emailHint.send")}
                        loading={sendState === "sending"}
                        disabled={sendState === "sending" || sendState === "THROTTLED"}
                        onClick={() => void onSend()}
                    />
                    {profileLink}
                </div>
            </>
        );
    }

    return (
        <div
            id="emailVerificationNotice"
            role="status"
            className="absolute right-4 top-full z-10 w-96 p-3 text-sm rounded-border shadow-md border border-surface-200 dark:border-surface-600 bg-surface-0 dark:bg-surface-800"
        >
            <div className="flex items-start gap-2">
                <i className="pi pi-exclamation-circle mt-1" aria-hidden />
                <div className="grow">{body}</div>
                <Button
                    id="emailHintCloseBtn"
                    icon="pi pi-times"
                    rounded
                    text
                    size="small"
                    aria-label={t("header.emailHint.close")}
                    onClick={onClose}
                />
            </div>
        </div>
    );
}
